/**
 * CreateSkillDialog Component
 * Modal dialog for creating and editing skills
 */

import React from 'react';
import { X, Plus, Edit2 } from 'lucide-react';
import { SkillForm } from './SkillForm';
import { useI18n } from '@/i18n';
import type { Skill, CreateSkillRequest, UpdateSkillRequest } from '@/types/skills';

interface CreateSkillDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSkillRequest | UpdateSkillRequest) => void;
  skill?: Skill | null;
  isSubmitting?: boolean;
}

export const CreateSkillDialog: React.FC<CreateSkillDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  skill,
  isSubmitting = false,
}) => {
  const { t } = useI18n();

  if (!isOpen) return null;

  const isEditing = !!skill;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-gray-500" />
            ) : (
              <Plus className="w-5 h-5 text-gray-500" />
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? t('agentConfig.skills.editTitle') : t('agentConfig.skills.createTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4">
          <SkillForm
            skill={skill}
            onSubmit={onSubmit}
            onCancel={onClose}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};
